import { defineComponent as _defineComponent } from 'vue'
import Modal from "../Modal.vue";
  import {
    computed,
    reactive,
    ref,
    watchEffect,
    onMounted,
  } from "vue";
  import CheckboxInput from "../input/CheckboxInput.vue";
  import useVuelidate from "@vuelidate/core";
  import { required, email, helpers } from "@vuelidate/validators";
  import Cleave from "vue-cleave-component";
  import { phoneNumberOption, phonePlaceholder } from "@/helpers/cleavePlugin";
  import { validPhoneNumber } from "@/helpers/validation";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import store from "@/store";
  import LeadSourceEnum from "@/enums/LeadSourceEnum";
  import { POSITION, useToast } from "vue-toastification";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import { formatPrice } from "@/helpers/transformation/price";
  import HouseFeatures from "@/components/builder/HouseFeatures.vue";
  import { FacadeInterface } from "@/types/FacadeInterface";
  import { UpdateBuilderQuoteDto } from "@/types/BuilderQuote";
  import { calculateBuildPriceOrPackagePriceTotal } from "@/helpers/builder/calculateBuildPriceOrPackagePriceTotal";
  import KlaviyoErrorEnum from "@/enums/KlaviyoEnum";
  import axios, { AxiosError } from "axios";
  import parseAustralianNumberToInternational from "@/helpers/phone-number";
  import ErrorMessages from "@/constants/errorsMessages";
  import { handleError } from "@/helpers/klaviyo/klaviyo-error-handler.helper";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderRequestReserveModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    house: {
      type: Object,
      required: true,
    },
    lot: {
      type: Object,
      required: true,
    },
    quote: {
      type: Object,
      required: true,
    },
    estate: {
      type: Object,
      required: true,
    },
    builder: {
      type: Object,
      required: true,
    },
    isPhoneVerificationModal: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "send", "openPhoneVerificationModal"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();

  const emits = __emit;

  const props = __props;

  const rules = {
    firstName: { required },
    lastName: { required },
    email: { required, email },
    phone: {
      required,
      validPhoneNumber: helpers.withMessage(
        "Value must be valid phone number",
        validPhoneNumber
      ),
    },
  };

  const formFields = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    timeReference: props.quote?.timeReference,
    bestDescribesYou: props.quote?.bestDescribesYou,
    financeStatus: props.quote?.financeStatus,
    additionalRequest: "",
    emailSentToLandowner: true,
    isAdminPackage: false,
  };
  const form = reactive(Object.assign({}, formFields));
  const v$ = useVuelidate(rules, form);
  const isLoading = ref(false);
  const facade = ref<FacadeInterface>();
  const siteCosts = ref<number>(0);
  const selectedFacadeId = computed(
    () => store.getters["facade/selectedFacadeId"]
  );

  const getSiteCosts = () => {
    if (props.quote) {
      store
        .dispatch("builder/getSiteCost", {
          lotId: props.quote.selections.lot.lotId,
          builderId: props.quote.selections.builder.builderId,
          houseId: props.quote.selections.house.houseId,
        })
        .then((res) => {
          siteCosts.value = res.data.siteCost;
        })
        .catch(() => {
          toast.error("Fetching site costs failed", {
            position: POSITION.TOP_LEFT,
          });
        });
    }
  };

  const buildPriceWithFacade = computed(() => {
    const facade = props.house.facades.find(
      (facade) => facade._id === selectedFacadeId.value
    );
    const basePrice = Number(props.house.basePrice);
    const facadePrice = Number(facade?.price || 0);

    return calculateBuildPriceOrPackagePriceTotal(
      basePrice,
      facadePrice,
      siteCosts.value
    );
  });

  const TotalPriceWithFacade = computed(() => {
    const facade = props.house?.facades?.find(
      (facade) => facade._id === selectedFacadeId.value
    );
    const basePrice = Number(props.house?.basePrice || 0);
    const facadePrice = Number(facade?.price || 0);
    const lotPrice = Number(props.lot?.lotId?.price || 0);

    return calculateBuildPriceOrPackagePriceTotal(
      basePrice,
      facadePrice,
      siteCosts.value,
      lotPrice
    );
  });

  const isDisabledSecure = computed(
    () =>
      Boolean(!form.firstName.length) ||
      Boolean(!form.lastName.length) ||
      Boolean(!form.phone.length) ||
      Boolean(!form.email.length) ||
      v$.value.email.$error
  );

  const onClose = () => {
    resetVuelidateErrors(v$);
    emits("close");
  };

  const verifyProfile = async (email: string, phone: string) => {
    const normalizedPhone = parseAustralianNumberToInternational(phone);

    return store.dispatch("klaviyo/verifyProfile", {
      email,
      phone_number: normalizedPhone,
    });
  };

  const createProfileAndSendSms = async (email: string, phone: string) => {
    const normalizedPhone = parseAustralianNumberToInternational(phone);

    await store.dispatch("klaviyo/createProfileAndSendVerificationSms", {
      email,
      phone_number: normalizedPhone,
    });
    const body = props.quote.isAdminPackage
      ? createBodyRequestForPackage()
      : createBodyRequest();

    emits("openPhoneVerificationModal", body);
  };

  const submitQuote = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    try {
      isLoading.value = true;
      const body = createBodyRequest();
      const verifyResponse = await verifyProfile(form.email, form.phone);

      if (verifyResponse.isValid) {
        await store.dispatch("builder/securePackage", {
          quoteId: props.quote._id,
          data: body,
        });
        emits("send");
      } else {
        await createProfileAndSendSms(form.email, form.phone);
      }

      onClose();
    } catch (error) {
      handleError(error as AxiosError);
    } finally {
      isLoading.value = false;
    }
  };

  const submitPackage = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    try {
      isLoading.value = true;
      const body = createBodyRequestForPackage();
      const verifyResponse = await verifyProfile(form.email, form.phone);

      if (verifyResponse.isValid) {
        await store.dispatch("builder/sendQuote", body);
        emits("send");
      } else {
        await createProfileAndSendSms(form.email, form.phone);
      }

      onClose();
    } catch (error) {
      handleError(error as AxiosError);
    } finally {
      isLoading.value = false;
    }
  };

  const validateForm = async () => {
    const isValid = await v$.value.$validate();

    v$.value.$touch();
    return isValid;
  };

  const getFacade = async () => {
    try {
      isLoading.value = true;
      const data = await store.dispatch("facade/getFacadesByQuery", {
        _id: selectedFacadeId.value,
      });

      facade.value = data[0];
    } catch (error) {
      toast.error("An error occurred while getting the facades");
    } finally {
      isLoading.value = false;
    }
  };

  const createBodyRequest = () => {
    const { lot } = props;
    const { landownerId, lotId } = lot;

    const userSelections = {
      builder: {
        builderId: props.builder._id,
        name: props.builder.name,
        logoUrl: props.builder.logo,
      },
      house: {
        houseId: props.house._id,
        name: props.house.name,
        price: props.house.basePrice,
      },
      facade: {
        facadeId: facade.value?._id as string,
        name: facade.value?.name as string,
        url: facade.value?.url as string,
        price: facade.value?.price as number,
      },
      floorplan: {
        name: props.house.floorplans[0]?.name,
        url: props.house.floorplans[0].url,
      },
      lot: {
        lotId: lot.lotId._id,
        name: lot.lotId.title,
        price: lot.lotId.price,
      },
    };

    return {
      selections: userSelections,
      timeReference: undefined,
      bestDescribesYou: undefined,
      financeStatus: undefined,
      estateId: props.estate._id,
      landownerId: props.quote.landownerId ?? props.lot.lotId.landownerId,
      emailSentToLandowner: form.emailSentToLandowner,
      isAdminPackage: form.isAdminPackage,
      user: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email.toLowerCase(),
        phone: form.phone,
      },
    };
  };

  const createBodyRequestForPackage = () => {
    const { lot } = props;
    const { landownerId, lotId } = lot;

    const userSelections = {
      builder: {
        builderId: props.builder._id,
        name: props.builder.name,
        logoUrl: props.builder.logo,
      },
      house: {
        houseId: props.house._id,
        name: props.house.name,
        price: props.house.basePrice,
      },
      facade: {
        facadeId: facade.value?._id as string,
        name: facade.value?.name as string,
        url: facade.value?.url as string,
        price: facade.value?.price as number,
      },
      floorplan: {
        name: props.house.floorplans[0]?.name,
        url: props.house.floorplans[0].url,
      },
      lot: {
        lotId: lot.lotId._id,
        name: lot.lotId.title,
        price: lot.lotId.price,
      },
    };

    return {
      selections: [userSelections],
      additionalRequest: form.additionalRequest,
      timeReference: form.timeReference,
      bestDescribesYou: form.bestDescribesYou,
      financeStatus: form.financeStatus,
      estateTitle: props.estate.title,
      estateId: props.estate._id,
      landownerId: props.quote.landownerId ?? props.lot.lotId.landownerId,
      emailSentToLandowner: form.emailSentToLandowner,
      isAdminPackage: form.isAdminPackage,
      lead: {
        landownerId: props.quote.landownerId ?? props.lot.lotId.landownerId,
        estateId: props.estate._id,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email.toLowerCase(),
        phone: form.phone,
        source: LeadSourceEnum.ENQUIRY,
        watched: false,
      },
    };
  };

  watchEffect(() => {
    if (props.isOpen) {
      getFacade();
      getSiteCosts();
    }
  });

  onMounted(() => {
    getFacade();
    getSiteCosts();
  });

const __returned__ = { toast, emits, props, rules, formFields, form, v$, isLoading, facade, siteCosts, selectedFacadeId, getSiteCosts, buildPriceWithFacade, TotalPriceWithFacade, isDisabledSecure, onClose, verifyProfile, createProfileAndSendSms, submitQuote, submitPackage, validateForm, getFacade, createBodyRequest, createBodyRequestForPackage, Modal, computed, reactive, ref, watchEffect, onMounted, CheckboxInput, get useVuelidate() { return useVuelidate }, get required() { return required }, get email() { return email }, get helpers() { return helpers }, get Cleave() { return Cleave }, get phoneNumberOption() { return phoneNumberOption }, get phonePlaceholder() { return phonePlaceholder }, get validPhoneNumber() { return validPhoneNumber }, get resetVuelidateErrors() { return resetVuelidateErrors }, get store() { return store }, get LeadSourceEnum() { return LeadSourceEnum }, get POSITION() { return POSITION }, get useToast() { return useToast }, DropDown, get formatPrice() { return formatPrice }, HouseFeatures, get FacadeInterface() { return FacadeInterface }, get UpdateBuilderQuoteDto() { return UpdateBuilderQuoteDto }, get calculateBuildPriceOrPackagePriceTotal() { return calculateBuildPriceOrPackagePriceTotal }, get KlaviyoErrorEnum() { return KlaviyoErrorEnum }, get axios() { return axios }, get AxiosError() { return AxiosError }, get parseAustralianNumberToInternational() { return parseAustralianNumberToInternational }, get ErrorMessages() { return ErrorMessages }, get handleError() { return handleError } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})